import { call, select } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import DistanceRecord from "@mapmycustomers/shared/types/entity/Distance";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRangeType from "@mapmycustomers/shared/types/range/DateRange";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import EfficiencyTrackerCardData from "@app/scene/dashboard/store/cards/efficiencyTracker/EfficiencyTrackerCardData";
import { callApi } from "@app/store/api/callApi";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { getOrganizationId } from "@app/store/iam";
import { getTeamsOfEntireOrganization } from "@app/store/members";
import EfficiencyTrackerCardConfiguration, {
  EfficiencyTrackerAggregationType,
} from "@app/types/dashboard/cards/efficiencyTracker/EfficiencyTrackerCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import { granularityByDateRange } from "../common";

import getBucketCount from "./getBucketCount";
import getEfficiencyMetricDescriptor, {
  EfficiencyMetricDescriptor,
} from "./getEfficiencyMetricDescriptor";
import getScopeFilters from "./getScopeFilters";
import getSelectedFunnelIds from "./getSelectedFunnelIds";
import getWonStageIds from "./getWonStageIds";
import rollUpDistance from "./rollUpDistance";

interface AggregationItem {
  [aggregationField: string]: { value: number } | number | undefined;
  doc_count: number;
  key: number; // date in numeric format
}

const getAggregationValue = (item: AggregationItem, key: string): number => {
  const aggregation = item[key];
  return typeof aggregation === "object" && aggregation ? (aggregation.value ?? 0) : 0;
};

const buildAggregations = (descriptor: EfficiencyMetricDescriptor, granularity: string) => ({
  primaryGroup: {
    field: descriptor.dateField,
    interval: granularity,
    offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
    timezone: getLocalTimeZone(),
  },
  ...(descriptor.valueKind === "field" && descriptor.field ? { sum: [descriptor.field] } : {}),
});

const buildDateFilter = (descriptor: EfficiencyMetricDescriptor, start: Date, end: Date) => ({
  [descriptor.dateField]: { $gte: start.toISOString(), $lte: end.toISOString() },
});

const getBucketTotal = (
  item: AggregationItem,
  descriptor: EfficiencyMetricDescriptor,
  sumKey: string | undefined
): number =>
  descriptor.valueKind === "count"
    ? item.doc_count
    : sumKey
      ? getAggregationValue(item, sumKey)
      : 0;

// Builds the per-bucket history and the headline count for one date range. Count metrics
// average per time bucket; field metrics average per record. Used for both the current and
// the previous (trend) ranges.
const summarize = (
  items: AggregationItem[],
  descriptor: EfficiencyMetricDescriptor,
  sumKey: string | undefined,
  range: DateRangeType,
  granularity: string,
  aggregationType: EfficiencyTrackerAggregationType
): { count: number; history: Array<{ date: Date; total: number }> } => {
  const history = items.map((item) => ({
    date: parseApiDate(item.key),
    total: getBucketTotal(item, descriptor, sumKey),
  }));

  const isAverage = aggregationType === EfficiencyTrackerAggregationType.AVG;
  let count: number;
  if (descriptor.valueKind === "count") {
    const totalCount = items.reduce((result, item) => result + item.doc_count, 0);
    count = isAverage ? totalCount / getBucketCount(range, granularity) : totalCount;
  } else {
    const totalSum = items.reduce(
      (result, item) => result + (sumKey ? getAggregationValue(item, sumKey) : 0),
      0
    );
    const totalDocuments = items.reduce((result, item) => result + item.doc_count, 0);
    count = isAverage ? (totalDocuments ? totalSum / totalDocuments : 0) : totalSum;
  }

  return { count, history };
};

export function* fetchEfficiencyTrackerCardDataHelper(params: {
  callback: (data: EfficiencyTrackerCardData) => void;
  configuration: EfficiencyTrackerCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, extraFilters, preferences } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  const orgId: Organization["id"] = yield select(getOrganizationId);
  const teams: Team[] = yield select(getTeamsOfEntireOrganization);
  const scopeTeam = isTeamScope ? teams.find(({ id }) => id === scope!.teamId) : undefined;
  const userIds: User["id"][] = scope?.userId
    ? [scope.userId]
    : (scopeTeam?.users.map(({ id }) => id) ?? []);

  const granularity = granularityByDateRange[dateRange.range];
  const fieldModel = getFieldModelByEntityType(criteria.entityType ?? EntityType.ACTIVITY);
  const descriptor: EfficiencyMetricDescriptor = getEfficiencyMetricDescriptor(
    criteria,
    fieldModel
  );

  let bakedFilters: Record<string, unknown> = {};
  if (criteria.fieldType === "facetime") {
    bakedFilters = { reliability: { $ne: null } };
  } else if (criteria.fieldType === "wonRevenue") {
    const funnelStages: Record<number, Stage[]> = yield select(getFunnelStages);
    const funnels: Funnel[] = yield select(getFunnels);
    const allFunnelIds = funnels.map(({ id }) => id);
    const selectedFunnelIds = getSelectedFunnelIds(extraFilters, allFunnelIds);
    const wonStageIds = getWonStageIds(funnelStages, selectedFunnelIds);
    bakedFilters = { stageId: { $in: wonStageIds } };
  }

  const scopeFilters = getScopeFilters(descriptor.entityType, scope, userIds);

  const range = getDatesRange(dateRange.range, dateRange.subRange);
  const sumKey = descriptor.field ? `sum_${descriptor.field}` : undefined;

  const currentResponse: AggregatedListResponse<AnyEntity, AggregationItem[] | undefined> =
    yield callApi(descriptor.fetchMethod, orgId, {
      $aggs: buildAggregations(descriptor, granularity),
      $filters: {
        ...scopeFilters,
        ...bakedFilters,
        ...extraFilters,
        ...buildDateFilter(descriptor, range.startDate, range.endDate),
      },
      $limit: 0,
    });

  const { count, history } = summarize(
    currentResponse.aggregations ?? [],
    descriptor,
    sumKey,
    range,
    granularity,
    criteria.aggregationType
  );

  const result: EfficiencyTrackerCardData = { count, history };

  if (preferences.showTrend) {
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const previousResponse: AggregatedListResponse<AnyEntity, AggregationItem[] | undefined> =
      yield callApi(descriptor.fetchMethod, orgId, {
        $aggs: buildAggregations(descriptor, granularity),
        $filters: {
          ...scopeFilters,
          ...bakedFilters,
          ...extraFilters,
          ...buildDateFilter(descriptor, previousRange.startDate, previousRange.endDate),
        },
        $limit: 0,
      });
    const previous = summarize(
      previousResponse.aggregations ?? [],
      descriptor,
      sumKey,
      previousRange,
      granularity,
      criteria.aggregationType
    );
    result.previousHistory = previous.history;
    result.previousCount = previous.count;
  }

  if (granularity !== "hour") {
    const distanceFilters = (start: Date, end: Date) => ({
      date: {
        $gte: formatDate(start, "yyyy-MM-dd"),
        $lte: formatDate(end, "yyyy-MM-dd"),
        $timezone: getLocalTimeZone(),
      },
      ...(userIds.length ? { userId: { $in: userIds } } : {}),
    });

    const distanceResponse: ListResponse<DistanceRecord> = yield callApi("fetchDistance", orgId, {
      $filters: distanceFilters(range.startDate, range.endDate),
    });
    const buckets = history.map(({ date }) => date);
    result.distanceHistory = rollUpDistance(distanceResponse.data, buckets, granularity);
    result.distance = result.distanceHistory.reduce(
      (distanceTotal, bucket) => distanceTotal + bucket.distance,
      0
    );

    if (preferences.showTrend && result.previousHistory) {
      const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
      const previousDistanceResponse: ListResponse<DistanceRecord> = yield callApi(
        "fetchDistance",
        orgId,
        { $filters: distanceFilters(previousRange.startDate, previousRange.endDate) }
      );
      const previousBuckets = result.previousHistory.map(({ date }) => date);
      result.previousDistanceHistory = rollUpDistance(
        previousDistanceResponse.data,
        previousBuckets,
        granularity
      );
      result.previousDistance = result.previousDistanceHistory.reduce(
        (distanceTotal, bucket) => distanceTotal + bucket.distance,
        0
      );
    }
  }

  yield call(callback, result);
}
